import { Scope } from '../../../../../enums/scope'
import { isAuthorized } from '../../../../../utils/isAuthorized'
import { NavigationMenuId } from '../enums/navigationMenuId'
import { NavigationMenuUmbrellaId } from '../enums/navigationMenuUmbrellaId'
import { NavigationMenuVisibilityConditions } from '../hooks/useNavigationMenuVisibilityConditions'

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- unread at runtime by design: this array exists to derive VisibilityRuleId below, which would otherwise have to restate every member by hand
const visibilityRuleIds = [
  NavigationMenuId.AnnualReports,
  NavigationMenuId.Banking,
  NavigationMenuId.BankingReact,
  NavigationMenuId.BankTransactions,
  NavigationMenuId.Creditors,
  NavigationMenuId.Debtors,
  NavigationMenuId.DeliveryNotes,
  NavigationMenuId.Documents,
  NavigationMenuId.ExpertDocuments,
  NavigationMenuId.ExpertMyClient,
  NavigationMenuId.ExpertReports,
  NavigationMenuId.ExpertReportsSuppliersBalance,
  NavigationMenuId.ExpertReportsCustomersBalance,
  NavigationMenuId.ExpertReportsTrialBalance,
  NavigationMenuId.ExpertReportsProfitAndLoss,
  NavigationMenuId.ExpertReportsBalanceSheet,
  NavigationMenuId.ExpertReportsVatDeclarations,
  NavigationMenuId.ExpertReviewEntries,
  NavigationMenuId.ImportedInvoices,
  NavigationMenuId.LedgerEntries,
  NavigationMenuId.LegacyRecurringInvoices,
  NavigationMenuId.NoBankAccounts,
  NavigationMenuId.Payroll,
  NavigationMenuId.Purchases,
  NavigationMenuId.Overview,
  NavigationMenuId.QuickStart,
  NavigationMenuId.Receipts,
  NavigationMenuId.Reconciliation,
  NavigationMenuId.RecurringInvoices,
  NavigationMenuId.Suppliers,
  NavigationMenuId.SalesReport,
  NavigationMenuId.ThingsToDo,
  NavigationMenuId.VatDeclarations,
  NavigationMenuId.Vouchers,
  NavigationMenuUmbrellaId.Inbox,
  NavigationMenuUmbrellaId.UmbrellaSubscriptions,
] as const

type VisibilityRuleId = (typeof visibilityRuleIds)[number]

type NavigationMenuVisibilityRules = Record<
  VisibilityRuleId,
  (conditions: NavigationMenuVisibilityConditions) => boolean
>

const isFrenchOrganization = ({ countryId }: Pick<NavigationMenuVisibilityConditions, 'countryId'>) =>
  countryId === 'FR'

export const navigationMenuVisibilityRules: NavigationMenuVisibilityRules = {
  [NavigationMenuId.AnnualReports]: ({ isAnnualReportAvailable }) => isAnnualReportAvailable,
  [NavigationMenuId.Banking]: ({ isFlagBankReactEnabled }) => !isFlagBankReactEnabled,
  [NavigationMenuId.BankingReact]: ({ isFlagBankReactEnabled }) => isFlagBankReactEnabled,
  [NavigationMenuId.BankTransactions]: ({ isBankTransactionsFlagEnabled }) => isBankTransactionsFlagEnabled,
  [NavigationMenuId.Creditors]: ({ isInboxEnabled, isEInvoicingOnly, orgUsesReceiptsOnly }) =>
    !isInboxEnabled && !isEInvoicingOnly && !orgUsesReceiptsOnly,
  [NavigationMenuId.Debtors]: ({ isSourceBilly }) => isSourceBilly,
  [NavigationMenuId.DeliveryNotes]: ({ isSourceZervant, hasDeliveryNotes }) => isSourceZervant && hasDeliveryNotes,
  [NavigationMenuId.Documents]: ({ isDmsEnabled, isEInvoicingOnly }) =>
    isDmsEnabled && !isEInvoicingOnly && isAuthorized(Scope.DmsFileRead),
  [NavigationMenuId.ExpertMyClient]: ({ isMyClientEnabled }) => isMyClientEnabled,
  [NavigationMenuId.ExpertDocuments]: ({ isDmsEnabled }) => isDmsEnabled && isAuthorized(Scope.DmsFileRead),
  [NavigationMenuId.ExpertReports]: (conditions) =>
    isFrenchOrganization(conditions) && conditions.isExpertReportsEnabled,
  [NavigationMenuId.ExpertReportsSuppliersBalance]: (conditions) =>
    isFrenchOrganization(conditions) && conditions.isExpertReportsSuppliersBalanceEnabled,
  [NavigationMenuId.ExpertReportsCustomersBalance]: (conditions) =>
    isFrenchOrganization(conditions) && conditions.isExpertReportsCustomersBalanceEnabled,
  [NavigationMenuId.ExpertReportsTrialBalance]: (conditions) =>
    isFrenchOrganization(conditions) && conditions.isExpertReportsTrialBalanceEnabled,
  [NavigationMenuId.ExpertReportsProfitAndLoss]: (conditions) =>
    isFrenchOrganization(conditions) && conditions.isExpertReportsProfitAndLossEnabled,
  [NavigationMenuId.ExpertReportsBalanceSheet]: (conditions) =>
    isFrenchOrganization(conditions) && conditions.isExpertReportsBalanceSheetEnabled,
  [NavigationMenuId.ExpertReportsVatDeclarations]: (conditions) =>
    isFrenchOrganization(conditions) && conditions.isExpertReportsVatDeclarationsEnabled,
  [NavigationMenuId.ExpertReviewEntries]: ({ isReviewEntriesEnabled }) => isReviewEntriesEnabled,
  [NavigationMenuId.ImportedInvoices]: ({ isImportedInvoicesFeatureEnabled }) => isImportedInvoicesFeatureEnabled,
  [NavigationMenuId.LedgerEntries]: ({ hasLedgerEntriesAccess }) => hasLedgerEntriesAccess,
  [NavigationMenuId.LegacyRecurringInvoices]: ({ isSourceBilly }) => isSourceBilly,
  [NavigationMenuId.NoBankAccounts]: ({ hasActiveBookkeepingBankAccount, isReconciliationViewVisible }) =>
    !hasActiveBookkeepingBankAccount && isReconciliationViewVisible,
  [NavigationMenuId.Payroll]: ({ isSalaryAvailable }) => isSalaryAvailable,
  [NavigationMenuId.Purchases]: ({ isInboxEnabled, isEInvoicingOnly, orgUsesReceiptsOnly }) =>
    !isInboxEnabled && !isEInvoicingOnly && !orgUsesReceiptsOnly,
  [NavigationMenuId.Overview]: ({ isEInvoicingOnly }) => !isEInvoicingOnly,
  [NavigationMenuId.QuickStart]: ({ isQuickStartEligible, isQuickStartSkipped }) =>
    isQuickStartEligible && !isQuickStartSkipped,
  [NavigationMenuId.Receipts]: ({ isInboxEnabled }) => !isInboxEnabled,
  [NavigationMenuId.Reconciliation]: ({ hasActiveBookkeepingBankAccount, isReconciliationViewVisible }) =>
    hasActiveBookkeepingBankAccount && isReconciliationViewVisible,
  [NavigationMenuId.RecurringInvoices]: ({ isEInvoicingOnly, isSourceZervant }) => !isEInvoicingOnly && isSourceZervant,
  [NavigationMenuId.SalesReport]: ({ isSourceZervant }) => isSourceZervant,
  [NavigationMenuId.Suppliers]: ({ orgUsesReceiptsOnly }) => !orgUsesReceiptsOnly,
  [NavigationMenuId.ThingsToDo]: ({ isInboxEnabled }) => isInboxEnabled,
  [NavigationMenuId.VatDeclarations]: ({ countryId, isVatExempted }) =>
    countryId !== 'DE' && !(countryId === 'FR' && isVatExempted),
  [NavigationMenuId.Vouchers]: ({ isInboxEnabled }) => isInboxEnabled,
  [NavigationMenuUmbrellaId.Inbox]: ({ isInboxEnabled }) => isInboxEnabled,
  // TODO: [NavigationMenuUmbrellaId.UmbrellaSubscriptions] Remove when all users migrated to a new scope version
  [NavigationMenuUmbrellaId.UmbrellaSubscriptions]: ({ isAdmin, isUmbrellaOwner }) => isAdmin || isUmbrellaOwner,
}
